
import { PriceAlert } from '@/types/price';
import { supabase } from '@/lib/supabase';
import { getAnonAlertSession } from '@/utils/anonAlertSession';

class PriceAlertService {
  /**
   * Crea un nuovo avviso di prezzo e lo salva nel database.
   * - Utenti autenticati: scoping via user_id.
   * - Utenti anonimi: scoping via session_token (header x-anon-session via client supabase).
   */
  async createAlert(alert: Omit<PriceAlert, 'id'>): Promise<PriceAlert> {
    try {
      const isAnon = !alert.userId;

      // Mapping dei campi per il database
      const dbAlert: Record<string, unknown> = {
        user_id: alert.userId ?? null,
        metal: alert.metal === 'gold' ? 'ORO' : 'ARGENTO',
        price: alert.targetPrice,
        type: alert.condition === 'above' ? 'SOPRA' : 'SOTTO',
        active: alert.isActive ?? true,
      };

      if (isAnon) {
        dbAlert.session_token = getAnonAlertSession();
      }

      const { data, error } = await supabase
        .from('alerts')
        .insert([dbAlert])
        .select()
        .single();

      if (error) {
        console.error('[PriceAlertService] Database error:', error);
        throw new Error('Impossibile salvare l\'avviso di prezzo');
      }

      // Mapping dei campi dal database al tipo TypeScript
      const newAlert: PriceAlert = {
        id: data.id,
        userId: data.user_id,
        metal: data.metal === 'ORO' ? 'gold' : 'silver',
        targetPrice: data.price,
        condition: data.type === 'SOPRA' ? 'above' : 'below',
        isActive: data.active
      };

      // Tentativo asincrono di configurare notifiche push senza bloccare
      this.setupNotificationsAsync(newAlert).catch(error => {
        console.warn('[PriceAlertService] Failed to setup push notifications:', error);
      });

      return newAlert;
    } catch (error) {
      console.error('[PriceAlertService] Error creating alert:', error);
      throw error;
    }
  }

  /**
   * Carica tutti gli avvisi per un utente dal database.
   * Per anon, RLS filtra automaticamente via header x-anon-session.
   */
  async loadUserAlerts(userId: string): Promise<PriceAlert[]> {
    try {
      let query = supabase
        .from('alerts')
        .select('*')
        .order('created_at', { ascending: false });

      if (userId) {
        query = query.eq('user_id', userId);
      } else {
        // Anon: lascia che RLS scope via header
        query = query.is('user_id', null);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[PriceAlertService] Error loading alerts:', error);
        return [];
      }

      return data.map(alert => ({
        id: alert.id,
        userId: alert.user_id,
        metal: alert.metal === 'ORO' ? 'gold' : 'silver',
        targetPrice: alert.price,
        condition: alert.type === 'SOPRA' ? 'above' : 'below',
        isActive: alert.active,
        triggeredAt: alert.triggered_at
      }));
    } catch (error) {
      console.error('[PriceAlertService] Error loading user alerts:', error);
      return [];
    }
  }

  /**
   * Elimina un avviso dal database
   */
  async deleteAlert(alertId: string, userId: string): Promise<boolean> {
    try {
      let query = supabase
        .from('alerts')
        .delete()
        .eq('id', alertId);

      if (userId) {
        query = query.eq('user_id', userId);
      } else {
        // RLS scope via x-anon-session
        query = query.is('user_id', null);
      }

      const { error } = await query;

      if (error) {
        console.error('[PriceAlertService] Error deleting alert:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[PriceAlertService] Error deleting alert:', error);
      return false;
    }
  }

  /**
   * Aggiorna lo stato di un avviso nel database
   */
  async toggleAlert(alertId: string, userId: string, isActive: boolean, triggeredAt?: string): Promise<boolean> {
    try {
      const updateData: any = { active: isActive };
      
      // Se l'avviso viene disabilitato automaticamente, salva il timestamp
      if (!isActive && triggeredAt) {
        updateData.triggered_at = triggeredAt;
      }

      let query = supabase
        .from('alerts')
        .update(updateData)
        .eq('id', alertId);

      if (userId) {
        query = query.eq('user_id', userId);
      } else {
        query = query.is('user_id', null);
      }

      const { error } = await query;

      if (error) {
        console.error('[PriceAlertService] Error updating alert:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('[PriceAlertService] Error updating alert:', error);
      return false;
    }
  }

  /**
   * Configurazione notifiche push completamente asincrona e non-bloccante
   */
  private async setupNotificationsAsync(alert: PriceAlert): Promise<void> {
    try {
      const { notificationService } = await import('@/services/notifications/notificationService');
      
      if (!this.isOneSignalReady()) {
        console.log('[PriceAlertService] OneSignal not ready, skipping notification setup');
        return;
      }

      await Promise.race([
        this.configureNotificationTags(alert),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 3000)
        )
      ]);

    } catch (error) {
      console.warn('[PriceAlertService] OneSignal setup failed:', error);
    }
  }

  /**
   * Verifica se OneSignal è pronto senza bloccanti
   */
  private isOneSignalReady(): boolean {
    try {
      return typeof window !== 'undefined' && 
             'OneSignal' in window && 
             window.OneSignal !== undefined;
    } catch {
      return false;
    }
  }

  /**
   * Configura i tag di notifica in modo sicuro
   */
  private async configureNotificationTags(alert: PriceAlert): Promise<void> {
    if (!this.isOneSignalReady()) return;

    try {
      const { notificationService } = await import('@/services/notifications/notificationService');
      
      const hasPermission = await notificationService.isPushNotificationsEnabled();
      if (!hasPermission) {
        console.log('[PriceAlertService] No notification permission, skipping tags');
        return;
      }

      await notificationService.setPriceAlertTags(
        alert.userId || 'anonymous',
        alert.metal,
        alert.targetPrice,
        alert.condition
      );

    } catch (error) {
      console.warn('[PriceAlertService] Failed to set notification tags:', error);
    }
  }

  /**
   * Richiede permessi di notifica in modo non-bloccante
   */
  async requestNotificationPermissionAsync(): Promise<boolean> {
    try {
      const { notificationService } = await import('@/services/notifications/notificationService');
      
      const permission = await Promise.race([
        notificationService.requestPermission(),
        new Promise<boolean>((resolve) => 
          setTimeout(() => resolve(false), 5000)
        )
      ]);

      return permission;
    } catch (error) {
      console.warn('[PriceAlertService] Permission request failed:', error);
      return false;
    }
  }
}

export const priceAlertService = new PriceAlertService();
